import { Accordion, AccordionItem } from '@components/layout/Accordion';
import IconLink from '@icons/external-link-alt.svg';
import { DependencyUsage } from '@modules/Dependencies/api/models/dependency-usage.model';
import { Dependency } from '@modules/Dependencies/api/models/dependency.model';
import { angularService } from '@utils/angularBridge';
import groupBy from 'lodash/groupBy';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { DataContext } from '../../../../modules/Workflows/api';
import { getEntityTransitionUrl } from '../utils/entityTransitionRules';
import { getRequestMetadata } from '../utils/urlLocationEntityRules';

const Styles = styled.div`
  &&&&& {
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 15px;
    color: ${props => props.theme.colors.road_grey_900};

    .header-row,
    .current-entity-item {
      display: flex;
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .name {
      cursor: pointer;
      font-weight: 500;
      font-size: 12px;
      line-height: 19px;
      color: ${props => props.theme.colors.base_grey};
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .current-entity-item {
      font-size: 11px;
      line-height: 12px;

      .current-entity-item-name {
        cursor: pointer;
        font-weight: 500;
        color: ${props => props.theme.colors.base_grey};
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .type, .current-entity-item-type {
      word-break: keep-all;
      white-space: nowrap;
    }

    .open-icon {
      margin-left: auto;
      padding: 7px;
      cursor: pointer;

      path {
        fill: ${props => props.theme.colors.road_grey_700};
      }
    }

    .used-in-sections {
      .k-panelbar-item-text {
        text-transform: none;
        font-size: 12px;
        line-height: 15px;
        color: ${props => props.theme.colors.road_grey_900};
      }

      .k-panelbar-header {
        .k-animation-container {
          margin-top: 10px;
        }

        &:last-child {
          .k-panelbar-content:last-child {
            margin-bottom: 0;
            border-bottom: none;
          }
        }
      }

      .k-panelbar-group {
        margin-bottom: 0;
      }

      .used-in-section {
        .k-panelbar-content {
          padding: 0 !important;
        }
      }

      .used-in-item {
        display: block;
        padding: 10px 10px 10px 20px;
        color: ${props => props.theme.colors.road_grey_900};
        font-style: normal;
        cursor: pointer;
        text-decoration: none;

        &:hover, &.selected {
          background: ${props => props.theme.colors.brand_primary_blue_light};
        }

        .name {
          font-weight: 500;
          font-size: 12px;
          line-height: 19px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .description {
          font-weight: normal;
          font-size: 12px;
          line-height: 15px;
        }
      }
    }
  }
`;

function buildEntityUrl(item: Dependency, child?: DependencyUsage) {
    const buildUrlFn = getEntityTransitionUrl[item.EntityType as any] || (() => '?none-target-url');
    const url = buildUrlFn(item, child);
    return url;
}

function buildCurrentEntityItemForDependency(dep: Dependency) {
    if (dep.RefType === 'Error') {
        return null;
    }

    const current = getRequestMetadata();
    switch (current.entityType) {
        case 'Workflow':
            const editWorkflowService = angularService('editWorkflowService');
            const dataContext: DataContext = editWorkflowService.dataContext;
            const wfUrl = `#/workflows/edit/${current.entityName}/${current.entityVersion}`;
            switch (dep.EntityType) {
                case 'DataProvider':
                    const url = dataContext.IsEmbedded
                                ? `${wfUrl}?openView&itemId=DataContext&itemType=DataContext&selectDataSource=${dep.DataSource}`
                                : `#/prototypes/edit/${dataContext.Name}?selectDataSource=${dep.DataSource}`;

                    return {
                        type: 'In data source',
                        name: dep.DataSource,
                        url: url
                    };
                case 'DataContext':
                    return null;
                default:
                    return {
                        type: 'In flow item',
                        name: dep.FlowItemName,
                        url: `${wfUrl}?itemId=${dep.FlowItemName}`
                    };
            }
    }
}

const plural = (text: string) => text + 's';

type ItemProps = {
    item: Dependency,
    handleClick?: (item: Dependency, usage?: DependencyUsage) => any,
    showCurrentItemLocation?: boolean,
};

export function Item({ item, handleClick, showCurrentItemLocation }: ItemProps) {
    const url = useMemo(() => {
        return buildEntityUrl(item);
    }, [item]);

    const asDisplayType = type => type.split(/(?=[A-Z])/).join(' ');

    const entityType = useMemo(() => {
        const type = asDisplayType(item.EntityType || 'Workflow');
        return type === 'Package' ? 'Remote Function' : type;
    }, [item.EntityType]);

    const usageSections = useMemo(() => {
        const groups = groupBy(item.UsedIn || [], child => {
            return ['InputScript'].includes(child.ScriptType) ? 'Script' : child.ScriptType;
        });
        const requestMetadata = getRequestMetadata();
        const from = item.RefType === 'Using'
                     ? {
                         EntityType: requestMetadata.entityType as any,
                         Name: requestMetadata.entityName,
                         Version: requestMetadata.entityVersion
                     } as Dependency
                     : item;
        const types = Object.keys(groups).sort();
        const result = types.map(type => ({
            Type: asDisplayType(type),
            Items: groups[type].map(child => ({
                ...child,
                Url: buildEntityUrl(from, child)
            }))
        }));
        return result;
    }, [item.UsedIn]);


    function onClick() {
        handleClick && handleClick(item);
    }

    function handleUsageClick(usage: DependencyUsage) {
        handleClick && handleClick(item, usage);
    }

    // function debugUsageClick(usage: DependencyUsage) {
    //     const url = buildEntityUrl(item, usage);
    //     window.open(url, '_blank');
    // }

    const currentEntityItemForDependency = showCurrentItemLocation !== false && !usageSections?.length
                                  ? buildCurrentEntityItemForDependency(item)
                                  : null;

    function debugUsageLocation(e) {
        e.preventDefault();
        e.stopPropagation();

        const usageLocation = buildCurrentEntityItemForDependency(item);
        const i = 0;
    }

    return <Styles>
        <div className="header-row">
            <div className="type">{entityType}</div>

            <a className="name" href={url} onClick={onClick} title={item.DisplayName || item.Name || ''}>
                {item.DisplayName || item.Name}
            </a>

            <a href={url} target="_blank" className="open-icon">
                <IconLink/>
            </a>
        </div>

        {currentEntityItemForDependency ? (
            <div className="current-entity-item">
                <span className="current-entity-item-type">{currentEntityItemForDependency.type}</span>
                {currentEntityItemForDependency?.url ? (
                    <a className="current-entity-item-name"
                       title={currentEntityItemForDependency.name as string}
                       href={currentEntityItemForDependency.url}
                    >
                        {currentEntityItemForDependency.name}
                    </a>
                ) : null}
            </div>
        ) : null}

        {usageSections.length ? (
            <Accordion flat className="used-in-sections">
                {usageSections.map((section, index) => (
                    <AccordionItem title={plural(section.Type)} className="used-in-section"
                                   key={`${section.Type}-${index}`}>
                        {section.Items.map((usage, index) => (
                            <a className="used-in-item" key={index}
                               href={usage.Url}
                               onClick={() => handleUsageClick(usage)}
                            >
                                <div className="name" title={usage.Name || ''}>{usage.Name}</div>
                                <div className="description">{usage.Description}</div>
                            </a>
                        ))}
                    </AccordionItem>
                ))}
            </Accordion>
        ) : null}
    </Styles>;
}
